import { useCallback, useEffect, useState } from 'react';
import { AdditionalItems, Prs2, ShienKankei } from 'src/types/common/Prs2';
import { getPrsDetail } from 'src/utilities/restApi/common/prsDetail';
import { RequestGetPrsDetail } from 'src/utilities/restApi/common/types/prsDetail';

type HookParams = {
  initSearch?: boolean;
  searchParams?: RequestGetPrsDetail;
};

export const usePrs = ({ initSearch = true, searchParams }: HookParams) => {
  const [initialized, setInitialized] = useState(false);
  const [data, setData] = useState<Prs2>();

  const refreshData = useCallback(async (params: RequestGetPrsDetail) => {
    return new Promise<Prs2>((resolve, reject) => {
      getPrsDetail(params)
        .then((response) => {
          if (response.data?.prsDetail) {
            const prsDetail = response.data.prsDetail;
            const newData = {
              prsId: prsDetail.prsid,
              evaPrsId: prsDetail.evaprsid,
              fName: prsDetail.fname,
              sName: prsDetail.sname,
              kfName: prsDetail.kfname,
              ksName: prsDetail.ksname,
              status: prsDetail.status ?? undefined, 
              sex: prsDetail.sex ?? undefined,
              birthday: prsDetail.birthday ?? undefined,
              telNo: prsDetail.telno ?? undefined,
              address: prsDetail.address ?? undefined,
              subAddress: prsDetail.subaddress ?? undefined,
              agreement: prsDetail.agreement ?? undefined,
              prsKubun: prsDetail.prskubun ?? undefined,
              familyStructure: prsDetail.family_structure ?? undefined,
              solitudeCohabitation: prsDetail.solitude_cohabitation ?? undefined,
              primaryDoctor: prsDetail.primary_doctor ?? undefined,
              physicalDisabilityCertificate: prsDetail.physical_disability_certificate ?? undefined,
              rehabilitationNotebook: prsDetail.rehabilitation_notebook ?? undefined,
              mentalDisabilityHealthAndWelfareHandbook:
                prsDetail.mental_disability_health_and_welfare_handbook ?? undefined,
              nursingCareLevel: prsDetail.nursing_care_level ?? undefined,
              intractableDisease: prsDetail.intractable_disease ?? undefined,
              emergencyContactName: prsDetail.emergency_contact_name ?? undefined,
              emergencyContactRelationship: prsDetail.emergency_contact_relationship ?? undefined,
              emergencyContactAddress: prsDetail.emergency_contact_address ?? undefined,
              emergencyContactPhoneNumber: prsDetail.emergency_contact_phone_number ?? undefined,
              supporterRemarks: prsDetail.supporter_remarks ?? undefined,
              prsStatusId: prsDetail.prsstatusid,
              prsStatusName: prsDetail.prsstatusname,
              prsStatusColor: prsDetail.prsstatuscolor,
              prsStatusSortOrder: prsDetail.prsstatussortorder,
              shelterTypeCode: prsDetail.shelter_type_code ?? undefined,
              shelterId: prsDetail.shelterid ?? undefined,
              shelterName: prsDetail.sheltername ?? undefined,
              customShelterName: prsDetail.custom_shelter_name ?? undefined,
              prsMemo: prsDetail.prsmemo ?? undefined,
              jichitaiId: prsDetail.jichitaiid,
              jichitaiName: prsDetail.jichitainame,
              lastPrsStatusUpdatedAt: prsDetail.lastprsstatusupdatedat ?? undefined,
              lastCalledAt: prsDetail.lastcalledat ?? undefined,
              lastVisitedAt: prsDetail.lastvisitedat ?? undefined,
              lastEvacuatedAt: prsDetail.lastevacuatedat ?? undefined,
              delFlg: prsDetail.delflag,
              zip: prsDetail.zip ?? undefined,
              residentAgreement: prsDetail.resident_agreement ?? undefined,
              municipalityAgreement: prsDetail.municipality_agreement ?? undefined,
              telNo2: prsDetail.telno2 ?? undefined,
              fax: prsDetail.fax ?? undefined,
              travelMedicine: prsDetail.travel_medicine ?? undefined,
              emergencyContactPhoneNumber2: prsDetail.emergency_contact_phone_number2 ?? undefined,
              emergencyContactNameKana: prsDetail.emergency_contact_name_kana ?? undefined,
              emergencyContactSolitudeCohabitation: prsDetail.emergency_contact_solitude_cohabitation ?? undefined,
              emergencyContact2Name: prsDetail.emergency_contact2_name ?? undefined,
              emergencyContact2Relationship: prsDetail.emergency_contact2_relationship ?? undefined,
              emergencyContact2Address: prsDetail.emergency_contact2_address ?? undefined,
              emergencyContact2PhoneNumber: prsDetail.emergency_contact2_phone_number ?? undefined,
              emergencyContact2PhoneNumber2: prsDetail.emergency_contact2_phone_number2 ?? undefined,
              emergencyContact2NameKana: prsDetail.emergency_contact2_name_kana ?? undefined,
              emergencyContact2SolitudeCohabitation: prsDetail.emergency_contact2_solitude_cohabitation ?? undefined,
              emergencyContact3Name: prsDetail.emergency_contact3_name ?? undefined,
              emergencyContact3Relationship: prsDetail.emergency_contact3_relationship ?? undefined,
              emergencyContact3Address: prsDetail.emergency_contact3_address ?? undefined,
              emergencyContact3PhoneNumber: prsDetail.emergency_contact3_phone_number ?? undefined,
              emergencyContact3PhoneNumber2: prsDetail.emergency_contact3_phone_number2 ?? undefined,
              emergencyContact3NameKana: prsDetail.emergency_contact3_name_kana ?? undefined,
              emergencyContact3SolitudeCohabitation: prsDetail.emergency_contact3_solitude_cohabitation ?? undefined,
              soshiki: prsDetail.soshiki.map((x) => ({ soshikiId: x.soshikiid, soshikiName: x.soshikiname })),
              shienKankei: prsDetail.shienkankei.map(
                (item) =>
                  ({
                    shienKankeiId: item.shienkankeiid,
                    spId: item.spid ?? undefined,
                    soshikiId: item.soshikiid ?? undefined,
                  } as ShienKankei)
              ),
              plannedShelters: prsDetail.planned_shelters.map((x) => {
                // NOTE: custom_shelter_address2のみある場合はデータ不整合
                const customShelterAddress =
                  x.custom_shelter_address1 != null && x.custom_shelter_address2 != null
                    ? [x.custom_shelter_address1, x.custom_shelter_address2].join(' ').trim()
                    : x.custom_shelter_address1 != null && x.custom_shelter_address2 == null
                    ? x.custom_shelter_address1
                    : undefined;

                return {
                  disasterTypeNumber: x.disaster_type_number,
                  disasterTypeName: x.disaster_type_name,
                  shelterTypeCode: x.shelter_type_code ?? undefined,
                  shelterId: x.shelterid ?? undefined,
                  shelterName: x.shelter_name ?? undefined,
                  shelterAddress: x.shelter_address ?? undefined,
                  customShelterName: x.custom_shelter_name ?? undefined,
                  customShelterAddress,
                };
              }),
              additionalItems: prsDetail.additional_items.map(
                (item) =>
                  ({
                    additionalItemIndex: item.additionalitemindex,
                    additionalItemName: item.additionalitemname,
                    additionalItemValue: item.additionalitemvalue,
                  } as AdditionalItems)
              ),
            } as Prs2;

            setData(newData);
            resolve(newData);
          }
        })
        .catch((error) => reject(error))
        .finally(() => setInitialized(true));
    });
  }, []);

  useEffect(() => {
    if (initSearch && searchParams) {
      refreshData(searchParams);
    }
  }, []);

  return { data, refreshData, initialized };
};
